import { useDiscardChangesGuard } from '@components/v2/ui/DiscardChangesModal/hooks/useDiscardChangesGuard';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { SignatureInfo } from '../../../../lib/SiteLib';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import {
  PersonSignatureFormValues,
  buildPersonSignatureFormSchema,
} from '../personSignatureForm.schema';
import { buildPersonSignatureContact } from '../personSignatureForm.utils';

type UsePersonSignatureFormParams = {
  initialValues?: SignatureInfo;
  onSave: (values: SignatureInfo) => Promise<void>;
  onClose: () => void;
};

const buildDefaultValues = (initialValues?: SignatureInfo): PersonSignatureFormValues => ({
  role: initialValues?.role ?? '',
  email: initialValues?.email ?? '',
  phone: initialValues?.phone ? `${initialValues.countryCode ?? ''}${initialValues.phone}` : '',
  countryCode: initialValues?.countryCode ?? '',
});

const usePersonSignatureForm = ({
  initialValues,
  onSave,
  onClose,
}: UsePersonSignatureFormParams) => {
  const { translate } = useTranslation();

  const form = useForm<PersonSignatureFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(buildPersonSignatureFormSchema(translate)),
    values: buildDefaultValues(initialValues),
    resetOptions: { keepDirtyValues: true },
  });

  const isSaving = form.formState.isSubmitting;

  const { requestClose, discardModalProps } = useDiscardChangesGuard({
    hasUnsavedChanges: form.formState.isDirty,
    onDiscard: onClose,
  });

  const handleRequestClose = () => {
    if (isSaving) {
      return;
    }

    requestClose();
  };

  const handleExited = () =>
    form.reset(buildDefaultValues(initialValues), { keepDirtyValues: false });

  const save = form.handleSubmit(async (values) => {
    await onSave(buildPersonSignatureContact(values));
  });

  return {
    control: form.control,
    setValue: form.setValue,
    isSaving,
    discardModalProps,
    save,
    handleExited,
    handleRequestClose,
  };
};

export default usePersonSignatureForm;
